'use client';

import {
  subscribeToCartChanged,
  subscribeToCartRepriced,
} from '@lib/commerce/cartChanged';
import {
  clearMiniCartCache,
  invalidateMiniCartCache,
} from '@lib/commerce/miniCartCache';
import type { QueryClient } from '@tanstack/react-query';
import { useLayoutEffect, useRef } from 'react';
import type { MiniCartPersister } from './miniCartPersister';

type UseMiniCartCacheParams = {
  persister: MiniCartPersister;
  queryClient: QueryClient;
  /** NextAuth's status: a signed-out shopper's page clears the cache. */
  sessionStatus: 'loading' | 'authenticated' | 'unauthenticated';
  userId?: string;
  /** The org the island reads the cart for, once it may. */
  organizationId?: string;
  currency: string;
};

/**
 * Runs the island's persister (CPS-7696): restores what an earlier page
 * stored for this shopper and org, and drops it on any change that makes it
 * stale. Writes in this tab invalidate through createMiniCartQueryClient's
 * settle; in another tab, through that tab's own island, as storage is
 * shared.
 */
export const useMiniCartCache = ({
  persister,
  queryClient,
  sessionStatus,
  userId,
  organizationId,
  currency,
}: UseMiniCartCacheParams): void => {
  const currencyRef = useRef(currency);
  currencyRef.current = currency;
  // Layout effects, so both land before the provider below starts a read:
  // its cart query, keyed by user alone, already exists while the org is
  // unknown, and would fetch as the org arrives.
  useLayoutEffect(() => persister.start(queryClient), [persister, queryClient]);

  // Who the page is for: a user id, or `unauthenticated` for a guest.
  const shopper = sessionStatus === 'authenticated' ? userId : sessionStatus;
  const previousRef = useRef<{ shopper?: string; organizationId?: string }>({});
  useLayoutEffect(() => {
    if (shopper !== 'loading') {
      const previous = previousRef.current;
      // A signed-out page, or a sign-in (a guest cart merged into the
      // user's), this tab's or another's that NextAuth synced here.
      if (
        shopper === 'unauthenticated' ||
        (previous.shopper !== undefined && previous.shopper !== shopper)
      ) {
        clearMiniCartCache();
      } else if (
        organizationId &&
        previous.organizationId &&
        previous.organizationId !== organizationId
      ) {
        // Reading the cart for the new org moves it there.
        invalidateMiniCartCache();
      }
      previousRef.current = {
        shopper,
        // Withheld while the bridge token is renewed, which is no switch.
        organizationId:
          organizationId ??
          (shopper === previous.shopper ? previous.organizationId : undefined),
      };
    }
    persister.follow(
      userId && organizationId ? { userId, organizationId } : undefined,
      currencyRef.current
    );
  }, [persister, shopper, userId, organizationId]);

  // The PDP's writes signal here, and its reread may re-price the cart. A
  // layout effect, so this listener runs before the island's reread starts:
  // a read that started first would be fenced out.
  useLayoutEffect(() => {
    const unsubscribers = [
      subscribeToCartChanged(persister.invalidate),
      subscribeToCartRepriced(persister.invalidate),
    ];
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [persister]);
};
